import { FC, useState, useEffect } from "react";
import { Form, Button, Spinner, ListGroup, Badge, Alert } from "react-bootstrap";
import { NetworkAccountService } from "../../../shared/services/contentNetwork/networkAccounts/networkAccountService";
import { SubresourceItem } from "../../../shared/services/contentNetwork/networkAccounts/commands/cmdGetSubresources";
import { NetworkResource } from "@models/networkResource";
import showAlert from "../../../shared/utils/generic";

interface Props {
  organizationId: number;
  network: string;
  resourceType: string;
  onComplete: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const TYPE_TO_RESPONSE_KEY: Record<string, string> = {
  page: "pages",
  board: "boards",
  profile: "profiles",
  youtube_channel: "youtube_channels",
  instagram_account: "instagram_accounts",
};

const TYPE_LABEL_PLURAL: Record<string, string> = {
  page: "pages",
  board: "boards",
  profile: "profiles",
  youtube_channel: "YouTube channels",
  instagram_account: "Instagram accounts",
};

const SubresourcePickerStep: FC<Props> = ({
  organizationId,
  network,
  resourceType,
  onComplete,
  onBack,
  onCancel,
}) => {
  const [accounts, setAccounts] = useState<NetworkResource[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);

  const [subresources, setSubresources] = useState<SubresourceItem[]>([]);
  const [alreadyAddedIds, setAlreadyAddedIds] = useState<Set<string>>(new Set());
  const [loadingSubresources, setLoadingSubresources] = useState(false);
  const [subresourceError, setSubresourceError] = useState<string | null>(null);
  const [isFetchError, setIsFetchError] = useState(false);
  const [allAddedInfo, setAllAddedInfo] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState({ current: 0, total: 0 });

  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState("");
  const [manualExternalId, setManualExternalId] = useState("");

  // YouTube OAuth flow state
  const [oauthInProgress, setOauthInProgress] = useState(false);
  const [oauthChannels, setOauthChannels] = useState<SubresourceItem[]>([]);
  const [oauthResourceId, setOauthResourceId] = useState<number | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(null);

  const isOAuthType = resourceType === "youtube_channel";

  const accountService = new NetworkAccountService();

   
  useEffect(() => { loadAccounts(); }, []);

  useEffect(() => {
    if (selectedAccountId && !isOAuthType) {
      loadSubresources(selectedAccountId);
    } else {
      setSubresources([]);
      setSelectedIds(new Set());
    }
     
  }, [selectedAccountId]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const allAccounts = await accountService.listNetworkResources({
        organization_ids: [organizationId, 4],
        types: ["account"],
      });
      const filtered = allAccounts.filter(
        (acc) => acc.network.toLowerCase() === network.toLowerCase()
      );
      setAccounts(filtered);
    } catch (err) {
      console.error("Failed to load accounts:", err);
      showAlert("Failed to load accounts", "error", 3000);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadSubresources = async (accountId: number) => {
    setLoadingSubresources(true);
    setSubresourceError(null);
    setIsFetchError(false);
    setAllAddedInfo(null);
    setSelectedIds(new Set());
    try {
      const response = await accountService.getSubresources(accountId);
      const responseKey = TYPE_TO_RESPONSE_KEY[resourceType];
      const items: SubresourceItem[] = (responseKey && response[responseKey]) || [];

      const existingResources = await accountService.listNetworkResources({
        organization_ids: [organizationId],
        types: [resourceType],
        networks: [network],
      });
      const existingExternalIds = new Set(
        existingResources.map((r) => r.external_id)
      );

      const label = TYPE_LABEL_PLURAL[resourceType] || `${resourceType}s`;
      if (items.length > 0 && items.every((item) => existingExternalIds.has(item.id))) {
        setAllAddedInfo(
          `All ${label} from this account are already set up for this client. ` +
            `Select which one to use when configuring an engagement.`
        );
      }

      setAlreadyAddedIds(existingExternalIds);
      setSubresources(items);
    } catch (err) {
      console.error("Failed to load subresources:", err);
      setSubresourceError("Failed to load sub-resources. Please try again.");
      setIsFetchError(true);
    } finally {
      setLoadingSubresources(false);
    }
  };

  const selectableSubresources = subresources.filter(
    (s) => !alreadyAddedIds.has(s.id)
  );

  const toggleSelection = (id: string) => {
    if (alreadyAddedIds.has(id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === selectableSubresources.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableSubresources.map((s) => s.id)));
    }
  };

  const createAndLink = async (items: SubresourceItem[]) => {
    if (!selectedAccountId) return;

    setCreating(true);
    setCreateProgress({ current: 0, total: items.length });

    const failures: string[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      setCreateProgress({ current: i + 1, total: items.length });

      try {
        const newResource = new NetworkResource();
        newResource.organization_id = organizationId;
        newResource.network = network;
        newResource.type = resourceType;
        newResource.name = item.name;
        newResource.external_id = item.id;
        newResource.is_active = true;

        const created = await accountService.createNetworkResource(newResource);
        await accountService.linkNetworkResource(created.id, selectedAccountId);
      } catch (err) {
        console.error(`Failed to create/link ${item.name}:`, err);
        failures.push(item.name);
      }
    }

    setCreating(false);

    if (failures.length === 0) {
      showAlert(
        `Added ${items.length} resource${items.length > 1 ? "s" : ""} successfully`,
        "success",
        3000,
      );
      onComplete();
    } else if (failures.length < items.length) {
      showAlert(
        `Added ${items.length - failures.length} of ${items.length}. Failed: ${failures.join(", ")}`,
        "warning",
        5000,
      );
      onComplete();
    } else {
      showAlert("Failed to add resources", "error", 3000);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAccountId) return;

    if (manualMode) {
      if (!manualName || !manualExternalId) {
        showAlert("Please fill in all required fields", "error", 3000);
        return;
      }
      await createAndLink([{ id: manualExternalId, name: manualName }]);
    } else {
      if (selectedIds.size === 0) return;
      const selected = subresources.filter((s) => selectedIds.has(s.id));
      await createAndLink(selected);
    }
  };

  const [oauthError, setOauthError] = useState<string | null>(null);

  const cleanupOAuthResource = async () => {
    if (oauthResourceId) {
      try {
        await accountService.deleteNetworkResource(oauthResourceId);
      } catch (err) {
        console.error("Failed to clean up resource:", err);
      }
      setOauthResourceId(null);
      setOauthChannels([]);
      setOauthError(null);
    }
  };

  const handleOAuthConnect = async () => {
    if (!selectedAccountId) return;

    setOauthInProgress(true);
    setOauthError(null);

    // If retrying, clean up the old resource first
    if (oauthResourceId) {
      await cleanupOAuthResource();
    }

    let createdResourceId: number | null = null;

    try {
      // 1. Create blank youtube_channel resource
      const newResource = new NetworkResource();
      newResource.organization_id = organizationId;
      newResource.network = network;
      newResource.type = resourceType;
      newResource.name = "YouTube Channel (pending)";
      newResource.external_id = "";
      newResource.is_active = true;

      const created = await accountService.createNetworkResource(newResource);
      createdResourceId = created.id;
      setOauthResourceId(created.id);

      // 2. Link to parent account
      await accountService.linkNetworkResource(created.id, selectedAccountId);

      // 3. Get OAuth URL and open popup
      const { auth_url } = await accountService.getOAuthUrl(created.id);
      const popup = window.open(auth_url, "oauth", "width=600,height=800");

      if (!popup) {
        showAlert("Please allow popups to connect your YouTube account", "error", 5000);
        // Delete resource since we never got to OAuth
        await accountService.deleteNetworkResource(created.id);
        setOauthResourceId(null);
        setOauthInProgress(false);
        return;
      }

      // 4. Poll until popup closes
      await new Promise<void>((resolve) => {
        const interval = setInterval(() => {
          if (popup.closed) {
            clearInterval(interval);
            resolve();
          }
        }, 500);
      });

      // 5. Fetch available channels from the now-authed resource
      try {
        const response = await accountService.getSubresources(created.id);
        const channels = response.youtube_channels || [];
        setOauthChannels(channels);
      } catch (subErr) {
        console.error("Failed to fetch channels:", subErr);
        setOauthError("Failed to fetch YouTube channels. You can try connecting again.");
      }

      setOauthInProgress(false);
    } catch (err) {
      console.error("OAuth flow failed:", err);
      setOauthInProgress(false);

      // Delete resource on pre-OAuth errors (create/link failed)
      if (createdResourceId) {
        try {
          await accountService.deleteNetworkResource(createdResourceId);
        } catch (deleteErr) {
          console.error("Failed to clean up resource:", deleteErr);
        }
        setOauthResourceId(null);
      }
    }
  };

  const handleChannelSelect = async () => {
    if (!oauthResourceId || !selectedChannelId) return;

    const channel = oauthChannels.find((c) => c.id === selectedChannelId);
    if (!channel) return;

    setCreating(true);
    try {
      await accountService.updateNetworkResource(oauthResourceId, {
        name: channel.name,
        external_id: channel.id,
      } as Partial<NetworkResource>);

      showAlert("YouTube channel added successfully", "success", 3000);
      onComplete();
    } catch (err) {
      console.error("Failed to update resource with channel:", err);
      showAlert("Failed to save channel selection", "error", 3000);

      // Clean up
      try {
        await accountService.deleteNetworkResource(oauthResourceId);
      } catch (deleteErr) {
        console.error("Failed to clean up resource:", deleteErr);
      }
    } finally {
      setCreating(false);
      setOauthResourceId(null);
    }
  };

  // No accounts state
  if (!loadingAccounts && accounts.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="bi bi-exclamation-circle fs-1 text-muted d-block mb-3"></i>
        <p className="text-muted">
          No {network} accounts found. Create an account first.
        </p>
        <Button variant="outline-secondary" onClick={onBack}>
          <i className="bi bi-arrow-left me-2"></i>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Account Selection */}
      <Form.Group className="mb-4">
        <Form.Label>
          Select Account <span className="text-danger">*</span>
        </Form.Label>
        {loadingAccounts ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <span className="ms-2 text-muted">Loading accounts...</span>
          </div>
        ) : (
          <Form.Select
            value={selectedAccountId ?? ""}
            onChange={(e) =>
              setSelectedAccountId(e.target.value ? Number(e.target.value) : null)
            }
          >
            <option value="">Choose an account...</option>
            {accounts.map((acc) => (
              <option key={acc.id} value={acc.id}>
                {acc.name} ({acc.external_id})
              </option>
            ))}
          </Form.Select>
        )}
      </Form.Group>

      {/* OAuth type: YouTube channel flow */}
      {selectedAccountId && isOAuthType && !manualMode && (
        oauthChannels.length > 0 ? (
          // Channel selection after OAuth
          <div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Select a YouTube Channel</h6>
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={handleOAuthConnect}
                disabled={oauthInProgress}
              >
                Connect different account
              </Button>
            </div>
            <ListGroup>
              {oauthChannels.map((channel) => (
                <ListGroup.Item
                  key={channel.id}
                  action
                  active={selectedChannelId === channel.id}
                  onClick={() => setSelectedChannelId(channel.id)}
                  className="d-flex align-items-center gap-3"
                >
                  <Form.Check
                    type="radio"
                    checked={selectedChannelId === channel.id}
                    onChange={() => setSelectedChannelId(channel.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div>
                    <div>{channel.name}</div>
                    <small className={selectedChannelId === channel.id ? "text-white-50" : "text-muted"}>
                      <i className="bi bi-hash"></i>
                      {channel.id}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        ) : (
          // Connect button — shown before OAuth, on error, or when no channels found
          <div className="text-center py-4">
            <i className="bi bi-youtube fs-1 text-danger d-block mb-3"></i>
            <p className="text-muted mb-3">
              {oauthError
                ? oauthError
                : oauthResourceId && !oauthInProgress
                  ? "No YouTube channels found on this account. Try connecting again or use a different account."
                  : "Connect your YouTube account to discover available channels."}
            </p>
            <Button
              variant="danger"
              onClick={handleOAuthConnect}
              disabled={oauthInProgress}
            >
              {oauthInProgress ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Connecting...
                </>
              ) : (
                <>
                  <i className="bi bi-youtube me-2"></i>
                  {oauthResourceId ? "Connect Again" : "Connect YouTube"}
                </>
              )}
            </Button>
          </div>
        )
      )}

      {/* Sub-resources: API picker or manual form */}
      {selectedAccountId && !isOAuthType && !manualMode && (
        <>
          {loadingSubresources ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
              <p className="text-muted mt-2 mb-0">Loading available resources...</p>
            </div>
          ) : subresourceError ? (
            <Alert variant={isFetchError ? "danger" : "info"} className="d-flex align-items-center gap-2">
              <i className={`bi ${isFetchError ? "bi-exclamation-circle" : "bi-info-circle"}`}></i>
              <span className="flex-grow-1">{subresourceError}</span>
              {isFetchError && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => loadSubresources(selectedAccountId)}
                >
                  Retry
                </Button>
              )}
            </Alert>
          ) : subresources.length === 0 ? (
            <Alert variant="info" className="d-flex align-items-center gap-2">
              <i className="bi bi-info-circle"></i>
              No resources found on this account.
            </Alert>
          ) : (
            <>
              {allAddedInfo && (
                <Alert variant="info" className="d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle"></i>
                  <span>{allAddedInfo}</span>
                </Alert>
              )}
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center gap-2">
                  <h6 className="mb-0">Available Resources</h6>
                  <Badge bg="secondary">{subresources.length}</Badge>
                </div>
                {selectableSubresources.length > 0 && (
                  <Button variant="link" size="sm" onClick={toggleAll} className="p-0">
                    {selectedIds.size === selectableSubresources.length
                      ? "Deselect All"
                      : "Select All"}
                  </Button>
                )}
              </div>
              <ListGroup style={{ maxHeight: "300px", overflowY: "auto" }}>
                {subresources.map((item) => {
                  const isAlreadyAdded = alreadyAddedIds.has(item.id);
                  const isSelected = selectedIds.has(item.id);
                  return (
                    <ListGroup.Item
                      key={item.id}
                      action={!isAlreadyAdded}
                      active={isSelected}
                      disabled={isAlreadyAdded}
                      onClick={() => toggleSelection(item.id)}
                      className="d-flex align-items-center gap-3"
                    >
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        disabled={isAlreadyAdded}
                        onChange={() => toggleSelection(item.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2">
                          <span>{item.name}</span>
                          {isAlreadyAdded && (
                            <Badge bg="success" pill>
                              Already added
                            </Badge>
                          )}
                        </div>
                        <small className={isSelected ? "text-white-50" : "text-muted"}>
                          <i className="bi bi-hash"></i>
                          {item.id}
                        </small>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </>
          )}
        </>
      )}

      {/* Manual form */}
      {selectedAccountId && manualMode && (
          <div>
            <Form.Group className="mb-3">
              <Form.Label>
                Resource Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter resource name"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                External ID <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter external ID from the network"
                value={manualExternalId}
                onChange={(e) => setManualExternalId(e.target.value)}
              />
              <Form.Text className="text-muted">
                The ID from the ad network (e.g., page ID, board ID)
              </Form.Text>
            </Form.Group>
          </div>
      )}

      {/* Footer Actions */}
      <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={async () => { await cleanupOAuthResource(); onBack(); }} disabled={creating || oauthInProgress}>
            <i className="bi bi-arrow-left me-2"></i>
            Back
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => setManualMode(!manualMode)}
            disabled={creating}
          >
            <i className={`bi ${manualMode ? "bi-list-check" : "bi-pencil"} me-2`}></i>
            {manualMode ? "Browse" : "Manual"}
          </Button>
        </div>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={async () => { await cleanupOAuthResource(); onCancel(); }} disabled={creating || oauthInProgress}>
            Cancel
          </Button>
          {isOAuthType && oauthChannels.length > 0 && !manualMode ? (
            <Button
              variant="primary"
              onClick={handleChannelSelect}
              disabled={creating || !selectedChannelId}
            >
              {creating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Select Channel
                </>
              )}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={creating || (manualMode ? !manualName || !manualExternalId : selectedIds.size === 0)}
            >
              {creating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Adding {createProgress.current} of {createProgress.total}...
                </>
              ) : manualMode ? (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Resource
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Selected ({selectedIds.size})
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubresourcePickerStep;
